// リッチUIコンポーネントの識別子と表示名。
// リッチUIデザイナーのナビと、ダッシュボードのコンポーネント別効果指標で
// 同じ名前を出すため、ここを単一ソースにする。
// キーは計測イベント（purchase_assist_component_events.component_type）の値と一致させること。

export type RichUiComponentKey =
  | 'product_gallery'
  | 'hero_card'
  | 'comparison_table'
  | 'comparison_radar'
  | 'personalized_cards'
  | 'quick_choice'
  | 'filter_slider'
  | 'review_highlights'
  | 'bundle_offer'

export type RichUiComponent = {
  key: RichUiComponentKey
  /** リッチUIデザイナーのナビ表示名（番号・記号つき） */
  navLabel: string
  /** 一覧・グラフ用の短い名称 */
  label: string
  /** プリセット管理を内包するコンポーネントの識別子 */
  preset?: 'hero' | 'quick_choice' | 'bundle'
  /**
   * 表示に外部の契約が要るコンポーネント。値はサイトの契約フラグ名。
   * 「データ準備中」ではなく**契約しているかどうか**を画面に出すために使う
   * （運用者にとっては「うちは契約しているのか」が知りたいことで、
   * 「データが準備中」では自分で解消できるのか分からない）。
   */
  requiresContract?: 'enable_reco_media' | 'enable_review'
}

/** 契約フラグ → 画面に出す機能名 */
export const CONTRACT_LABELS: Record<string, string> = {
  enable_reco_media: 'Reco-Media',
  enable_review: 'UGC（レビュー）',
}

export const RICH_UI_COMPONENTS: RichUiComponent[] = [
  { key: 'product_gallery', navLabel: '① 商品ギャラリー', label: '商品ギャラリー' },
  { key: 'hero_card', navLabel: '② Heroカード ★', label: 'Heroカード', preset: 'hero' },
  { key: 'comparison_table', navLabel: '③ 比較テーブル', label: '比較テーブル' },
  { key: 'comparison_radar', navLabel: '④ レーダーチャート', label: 'レーダーチャート' },
  { key: 'personalized_cards', navLabel: '⑤ パーソナライズ', label: 'パーソナライズ', requiresContract: 'enable_reco_media' },
  { key: 'quick_choice', navLabel: '⑥ 二択カード ★', label: '二択カード', preset: 'quick_choice' },
  { key: 'filter_slider', navLabel: '⑦ フィルタスライダー', label: 'フィルタスライダー' },
  { key: 'review_highlights', navLabel: '⑧ レビューハイライト', label: 'レビューハイライト', requiresContract: 'enable_review' },
  { key: 'bundle_offer', navLabel: '⑩ バンドル ★', label: 'バンドル', preset: 'bundle' },
]

const BY_KEY = new Map(RICH_UI_COMPONENTS.map(c => [c.key as string, c]))

/** 計測イベントの component_type から表示名を引く。未知の値はそのまま返す（取りこぼしを隠さない）。 */
export const richUiComponentLabel = (componentType: string): string =>
  BY_KEY.get(componentType)?.label ?? componentType

/** 一覧の並び順（リッチUIデザイナーのナビ順）。未知の値は末尾に寄せる。 */
export const richUiComponentOrder = (componentType: string): number => {
  const i = RICH_UI_COMPONENTS.findIndex(c => c.key === componentType)
  return i < 0 ? RICH_UI_COMPONENTS.length : i
}
